// File: js/db/logout.js
//
// A logout that cannot destroy money — the safe replacement for `app.js:443-465`
// (audit C5).
//
// The current `performLogout()` does `await db.delete()` and then re-declares the
// schema. Two separate disasters ride on that one line:
//
//   1. Every `sync_status:'pending'` row — money already collected, receipts
//      already printed and handed to patients — is wiped with no warning and no
//      pending check. The cashier sees « Déconnexion réussie ».
//   2. The re-declared store is one of FOUR conflicting `db.version(1)` schemas,
//      so from that moment on every offline save throws `DataError` until the user
//      clears browser storage. `schema.js` fixes the second half; this module
//      fixes the first.
//
// The rule, from spec §7: logout REFUSES while the outbox is non-empty, and it
// NEVER drops the ledger or outbox tables. It clears only what is safe to lose —
// the reference-data caches, which are refilled from the server on next sign-in.
//
// What deliberately SURVIVES a logout:
//   • `ledger` and `outbox` — the money. Obviously.
//   • `conflicts` — parked events a human still has to resolve.
//   • `meta.block` — the receipt block belongs to the DEVICE, not to the user who
//     happened to be signed in. Clearing it would trigger the `meta_loss` path and
//     abandon the unused tail of a perfectly good range on every shift change.
//   • `meta.device_id`, `meta.persist_granted`, `meta.last_pull_at` — device
//     facts, not session facts.

import { pendingSummary } from './outbox-read.js';
import { LogoutRefusedError } from './errors.js';

/** Caches that hold no money and are refilled from the server after sign-in. */
const CLEARABLE_TABLES = ['users', 'revenueTypes', 'drugs'];

/**
 * Whether this device may log out right now.
 *
 * Ask BEFORE showing a logout button, so the cashier learns about the queue
 * before they are half-way out the door.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<{allowed:boolean, reason?:'unsynced-outbox', pendingCount:number, pendingTotal:number}>}
 */
export async function canLogout(db) {
    const { count, total } = await pendingSummary(db);
    if (count > 0) {
        return { allowed: false, reason: 'unsynced-outbox', pendingCount: count, pendingTotal: total };
    }
    return { allowed: true, pendingCount: 0, pendingTotal: 0 };
}

/**
 * Log out safely.
 *
 * Refuses — by throwing `LogoutRefusedError`, never by returning a soft failure
 * the caller can ignore — while the outbox holds anything at all. On success it
 * clears the reference-data caches only, then calls the injected `signOut`.
 *
 * `signOut` is injected so this module never imports `supabaseClient.js` (which
 * touches `document` at module scope) and so the refusal logic is testable with no
 * network. Sign-out runs AFTER the local clear: signing out first and then failing
 * to clear leaves a device that is neither signed in nor clean.
 *
 * @param {import('dexie').Dexie} db
 * @param {{signOut?: () => Promise<*>}} [deps]
 * @returns {Promise<{ok:true, cleared:string[]}>}
 * @throws {LogoutRefusedError} While the outbox is non-empty.
 */
export async function performSafeLogout(db, deps = {}) {
    const gate = await canLogout(db);
    if (!gate.allowed) {
        throw new LogoutRefusedError({ pendingCount: gate.pendingCount, pendingTotal: gate.pendingTotal });
    }

    const cleared = await clearReferenceCaches(db);

    if (typeof deps.signOut === 'function') {
        await deps.signOut();
    }

    return { ok: true, cleared };
}

/**
 * Clear the reference-data caches and nothing else.
 *
 * Note what is NOT here: no `db.delete()`, no `ledger.clear()`, no
 * `outbox.clear()`, no `meta.clear()`. There is deliberately no parameter, flag or
 * escape hatch that would let a caller ask for those — a "force logout" option is
 * exactly how the unsynced-cash path grows back.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<string[]>} The tables actually cleared.
 */
export async function clearReferenceCaches(db) {
    const present = CLEARABLE_TABLES.filter(name => db.tables.some(t => t.name === name));
    if (present.length === 0) return [];
    await db.transaction('rw', present.map(name => db.table(name)), async () => {
        for (const name of present) await db.table(name).clear();
    });
    return present;
}
